import { useCallback, useEffect, useRef, useState } from "react";

import { X, Check } from "lucide-react";

import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { claudeAgentApi } from "@/lib/api/claude-agent";

/**
 * Session feedback "pulse" — a quiet, occasional check-in docked above the
 * composer. One tap rates the session; Bad / Many issues open a note. Positives
 * get a 5s change/undo window then commit. Cadence + eligibility are decided
 * server-side (GET /feedback/eligibility); this component only renders when the
 * server says show. See the agent-feedback-design memory.
 *
 * Only prompts during an ACTIVE session: it waits until the user has completed
 * at least one live turn this mount (a `false→true→false` streaming cycle), so
 * merely reopening an old project with lots of history never triggers it. And
 * it records NOTHING on appearance — the per-user cooldown starts only when the
 * user actually rates or explicitly closes (X) the pulse. Ignoring it /
 * navigating away burns nothing, so it can return next active session.
 */

type Rating = "excellent" | "good" | "bad" | "many_issues";

const RATINGS: { value: Rating; label: string }[] = [
  { value: "excellent", label: "Excellent" },
  { value: "good", label: "Good" },
  { value: "bad", label: "Bad" },
  { value: "many_issues", label: "Many issues" },
];

const NEGATIVE = new Set<Rating>(["bad", "many_issues"]);

const NOTE_SUB: Record<Rating, string> = {
  excellent: "Glad to hear it. Anything we should keep doing?",
  good: "Good to hear. Anything we could improve?",
  bad: "Sorry about that. What went wrong?",
  many_issues: "We'd really like to fix this. What went wrong?",
};
const NOTE_PLACEHOLDER: Record<Rating, string> = {
  excellent: "What worked well? (optional)",
  good: "What would make it better? (optional)",
  bad: "Tell us what happened…",
  many_issues: "What broke? What did you expect vs. get?",
};
const DONE_MSG: Record<Rating, string> = {
  excellent: "Thanks, great to hear.",
  good: "Thanks for the feedback.",
  bad: "Thanks, we're on it.",
  many_issues: "Thanks, we're on it. We read every one.",
};

/** Client pre-gate; the server enforces the real min-turns from config. */
const CLIENT_MIN_TURNS = 6;
const CHANGE_WINDOW_MS = 5000;

interface SessionFeedbackPulseProps {
  sessionId: string | null;
  /** Number of user turns in the session (drives the trigger). */
  turnCount: number;
  /** True while the agent is streaming — we only prompt when idle. */
  isStreaming: boolean;
}

export function SessionFeedbackPulse({
  sessionId,
  turnCount,
  isStreaming,
}: SessionFeedbackPulseProps) {
  const { getIdToken } = useAuth();

  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [rating, setRating] = useState<Rating | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [done, setDone] = useState<Rating | null>(null);
  // True once the user has completed a live turn in THIS session (a reply
  // finished streaming). Gates the prompt to active chats, not old-project opens.
  const [hadLiveTurn, setHadLiveTurn] = useState(false);

  // Re-check throttle: only hit eligibility once per new turn count.
  const lastCheckedTurnsRef = useRef<number>(-1);
  // Live-turn tracking: which session we're tracking + last streaming state.
  const liveSessionRef = useRef<string | null>(null);
  const prevStreamingRef = useRef(false);
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (commitTimer.current) clearTimeout(commitTimer.current);
    if (tickTimer.current) clearInterval(tickTimer.current);
    commitTimer.current = tickTimer.current = null;
    setSecondsLeft(null);
  }, []);

  // Reset everything when the session changes.
  useEffect(() => {
    clearTimers();
    lastCheckedTurnsRef.current = -1;
    setVisible(false);
    setLeaving(false);
    setRating(null);
    setNoteOpen(false);
    setComment("");
    setDone(null);
  }, [sessionId, clearTimers]);

  // Track whether an active (live) turn completed in this session. On a session
  // switch we reset and re-baseline the streaming state (so a reply already in
  // flight when we arrive isn't miscounted). Within the session, a streaming
  // cycle finishing (true→false) means the user sent a message and got a reply —
  // that's when the session becomes eligible to prompt. Owns `hadLiveTurn` alone
  // (the session-change branch returns early) so there's no cross-effect race.
  useEffect(() => {
    if (liveSessionRef.current !== sessionId) {
      liveSessionRef.current = sessionId;
      prevStreamingRef.current = isStreaming;
      setHadLiveTurn(false);
      return;
    }
    if (prevStreamingRef.current && !isStreaming) setHadLiveTurn(true);
    prevStreamingRef.current = isStreaming;
  }, [sessionId, isStreaming]);

  // Ask the server whether to show, when idle + a live turn has completed this
  // session + enough new turns. Re-checked as turns grow (no permanent
  // per-session lock) so a long session can re-ask after its cooldown; the
  // server enforces the real cadence. We record NOTHING on display — the
  // cooldown starts only when the user rates or closes (X). So an ignored /
  // navigated-away pulse costs nothing and can return next active session.
  useEffect(() => {
    if (!sessionId || isStreaming || visible || leaving) return;
    if (!hadLiveTurn) return;
    if (turnCount < CLIENT_MIN_TURNS) return;
    if (lastCheckedTurnsRef.current === turnCount) return;
    lastCheckedTurnsRef.current = turnCount;

    let cancelled = false;
    (async () => {
      try {
        const token = await getIdToken();
        if (!token || cancelled) return;
        const res = await claudeAgentApi.getFeedbackEligibility(token, sessionId);
        if (!cancelled && res.show) setVisible(true);
      } catch {
        // Non-fatal: never let a feedback check disrupt the chat.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, turnCount, isStreaming, visible, leaving, hadLiveTurn, getIdToken]);

  const collapse = useCallback(() => {
    setLeaving(true);
    setTimeout(() => setVisible(false), 320);
  }, []);

  const commit = useCallback(async () => {
    clearTimers();
    const chosen = rating;
    if (!sessionId || !chosen) return;
    setDone(chosen);
    try {
      const token = await getIdToken();
      if (token) {
        await claudeAgentApi.submitSessionFeedback(token, {
          sessionId,
          rating: chosen,
          comment: comment.trim() || undefined,
        });
      }
    } catch {
      // Non-fatal — the user's intent is captured optimistically in the UI.
    }
    setTimeout(collapse, 1400);
  }, [clearTimers, rating, sessionId, comment, getIdToken, collapse]);

  const startChangeWindow = useCallback(() => {
    clearTimers();
    setSecondsLeft(Math.round(CHANGE_WINDOW_MS / 1000));
    tickTimer.current = setInterval(() => {
      setSecondsLeft((s) => (s === null ? null : Math.max(0, s - 1)));
    }, 1000);
    commitTimer.current = setTimeout(() => {
      void commit();
    }, CHANGE_WINDOW_MS);
  }, [clearTimers, commit]);

  const pick = useCallback(
    (r: Rating) => {
      setRating(r);
      if (NEGATIVE.has(r)) {
        // The note IS the interaction → no timer; commit on Send / No thanks.
        clearTimers();
        setNoteOpen(true);
      } else {
        setNoteOpen(false);
        startChangeWindow();
      }
    },
    [clearTimers, startChangeWindow],
  );

  const undo = useCallback(() => {
    clearTimers();
    setRating(null);
    setNoteOpen(false);
  }, [clearTimers]);

  const dismiss = useCallback(async () => {
    clearTimers();
    collapse();
    if (!sessionId) return;
    try {
      const token = await getIdToken();
      if (token) await claudeAgentApi.dismissSessionFeedback(token, sessionId);
    } catch {
      // Non-fatal.
    }
  }, [clearTimers, collapse, sessionId, getIdToken]);

  if (!visible) return null;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-[14px] border border-border/60 bg-muted px-3 pb-3 pt-[11px] transition-all duration-300",
        leaving && "max-h-0 border-transparent pb-0 pt-0 opacity-0",
      )}
    >
      {done ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Check className="h-3.5 w-3.5" strokeWidth={2.4} />
          <span>{DONE_MSG[done]}</span>
        </div>
      ) : (
        <>
          <div className="mb-2.5 flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">
              How's this session going?
              <span className="ml-1.5 font-normal opacity-70">optional</span>
            </span>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Not now"
              className="inline-flex rounded-md p-1 text-muted-foreground/70 transition-colors hover:bg-accent hover:text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Rate the session">
            {RATINGS.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => pick(r.value)}
                className={cn(
                  "cursor-pointer rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors",
                  rating === r.value
                    ? "border-foreground bg-foreground text-background"
                    : "border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                {r.label}
              </button>
            ))}
          </div>

          {/* Positive change/undo window */}
          {secondsLeft !== null && (
            <div className="mt-2.5 flex items-center gap-2.5 text-[11.5px] text-muted-foreground">
              <span className="tabular-nums">Saving in {secondsLeft}s</span>
              <span className="h-[3px] max-w-[110px] flex-1 overflow-hidden rounded-full bg-border">
                <span
                  className="block h-full bg-muted-foreground"
                  style={{
                    width: "0%",
                    transition: `width ${CHANGE_WINDOW_MS}ms linear`,
                    animation: `optidev-fb-drain ${CHANGE_WINDOW_MS}ms linear forwards`,
                  }}
                />
              </span>
              <button
                type="button"
                onClick={undo}
                className="font-medium text-foreground underline underline-offset-2"
              >
                Undo
              </button>
            </div>
          )}

          {/* Note (bad / many issues, or optional positive) */}
          {noteOpen && rating && (
            <div className="mt-3">
              <p className="mb-2 text-xs text-muted-foreground">{NOTE_SUB[rating]}</p>
              <textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={NOTE_PLACEHOLDER[rating]}
                className="w-full resize-none rounded-[10px] border border-border bg-card px-2.5 py-2 text-[13px] leading-normal text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-muted-foreground/50"
              />
              <div className="mt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => void commit()}
                  className="cursor-pointer px-1 py-1.5 text-xs text-muted-foreground hover:text-foreground"
                >
                  No thanks
                </button>
                <button
                  type="button"
                  onClick={() => void commit()}
                  className="cursor-pointer rounded-lg bg-foreground px-3.5 py-1.5 text-xs font-semibold text-background hover:opacity-90"
                >
                  Send
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <style>{`@keyframes optidev-fb-drain { from { width: 100%; } to { width: 0%; } }`}</style>
    </div>
  );
}
